import React from "react"
import { Form, Select, Space, FormInstance } from "antd"
import { faFileCsv, faGear } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import FormatPanelHeader from "./FormatPanelHeader.component"
import { EXPORT_VARIABLE_TYPES, CSVFormValues, CSV_CAT_OPTION } from "types/engineExport.types"
import { InputValues } from "features/QueryConsole/api/useExportDataModal"

interface CsvExportPanelProps {
  inputValues: InputValues
  onInputChange: <K extends keyof InputValues>(key: K, value: InputValues[K]) => void
  onExport: () => Promise<void>
  isExporting: boolean
  form: FormInstance<CSVFormValues>
  onOpenExportMap: (format: "csv") => void
}

const CsvExportPanel: React.FC<CsvExportPanelProps> = ({
  inputValues,
  onInputChange,
  onExport,
  isExporting,
  form,
  onOpenExportMap,
}) => (
  <div className="flex flex-col">
    <FormatPanelHeader
      icon={faFileCsv}
      title="Export as CSV"
      tagLabel="Universal"
      tagColor="orange"
      description="Comma-separated values. Widely compatible with spreadsheet software, databases, and data tools."
    />
    <Form layout="vertical" form={form}>
      <Form.Item
        label="Select Variable Type"
        name="variableType"
        rules={[{ required: true, message: "Please select a Variable Type" }]}
      >
        <Select
          value={inputValues.csvVariableType || undefined}
          onChange={(value: EXPORT_VARIABLE_TYPES) =>
            onInputChange("csvVariableType", value)
          }
          options={[
            { value: EXPORT_VARIABLE_TYPES.NUMBERING, label: "Numbered" },
            { value: EXPORT_VARIABLE_TYPES.LABELS, label: "Labelled" },
          ]}
          placeholder="Select Variable Type"
        />
      </Form.Item>
      <Form.Item label="Categorical Export Format">
        <Select
          value={inputValues.csvCatOption}
          onChange={(value: CSV_CAT_OPTION) =>
            onInputChange("csvCatOption", value)
          }
          options={[
            { value: CSV_CAT_OPTION.NORMAL, label: "Normal (category codes, requires map file)" },
            { value: CSV_CAT_OPTION.EXPAND, label: "Expanded (boolean columns per category)" },
            { value: CSV_CAT_OPTION.SECOND_ROW, label: "Second Row (category definitions in row 2)" },
            { value: CSV_CAT_OPTION.LABELS_IN_DATA, label: "Labels in Data (human-readable labels)" },
          ]}
        />
      </Form.Item>
    </Form>
    <div className="border-t border-gray-100 pt-4 mt-1">
      <Space wrap>
        <DataQButton
          type="primary"
          onClick={onExport}
          isLoading={isExporting}
          aria-label="Export data as CSV file"
        >
          Export CSV
        </DataQButton>
        <DataQButton
          type="secondary"
          onClick={() => onOpenExportMap("csv")}
        >
          <FontAwesomeIcon icon={faGear} className="mr-1" />
          Configure Map
        </DataQButton>
      </Space>
    </div>
  </div>
)

export default CsvExportPanel
